import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _noop from 'lodash/noop';

import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';
import { media } from '@mlb/react-patterns/style/helpers/media.ts';
import { AnimateFrom } from '@mlb/react-patterns/components/AnimatedModal/animatedModal.types.ts';
import { ContentWrapper } from '@mlb/react-patterns/components/AnimatedModal/animatedModal.style.tsx';
import { Wrapper as HeaderWrapper } from '@mlb/react-patterns/components/AnimatedModal/Header/header.style.tsx';

import ModalPattern from 'bdvp-common/src/containers/Modal';
import { useKeyPress, useMedia } from 'bdvp-common/src/hooks';
import { useDictionary } from 'bdvp-common/src/hooks/config';
import { keyConstants } from 'bdvp-common/src/utils';
import { constants as modalConstants } from 'bdvp-graphql/src/resolvers/modals';

import ClipTrimming from '../../containers/ClipTrimming';

const { TRIM_REEL_CLIP_MODAL_NAME } = modalConstants;
const { TAB } = keyConstants;
const keysToTrack = [TAB];
const modalPosition = {
    bottom: 0,
    left: 0,
};

const Modal = styled(ModalPattern)`
    font-family: ${({ theme }) => theme.fonts.family.primary};

    @supports (padding-top: constant(safe-area-inset-top)) {
        --safe-area-inset-top: constant(safe-area-inset-top, 0);
        height: calc(99% - var(--safe-area-inset-top));
        padding-bottom: constant(safe-area-inset-bottom, 0);
    }

    @supports (padding-top: env(safe-area-inset-top)) {
        --safe-area-inset-top: env(safe-area-inset-top, 0);
        height: calc(99% - var(--safe-area-inset-top));
        padding-bottom: env(safe-area-inset-bottom, 0);
    }

    ${media.mdmin`
        top: 50%;
        left: 50%;
        height: 86vh;
        max-height: 723px;
        min-height: 450px;
        transform: translate(-50%, -50%);
        padding-bottom: 0;
    `}

    ${ContentWrapper} {
        height: 100%;
    }
    ${HeaderWrapper} {
        padding-bottom: 0;
    }
`;

const TrimmingModal = ({
    onClosed,
    onOpened,
}) => {

    const firstModalNode = React.useRef(null);
    const lastModalNode = React.useRef(null);
    const {
        trim_video: trimVideo = 'Trim Video',
    } = useDictionary();

    const keyPressCallbackProps = {
        cb: (event) => {

            if (event.target === lastModalNode.current) {

                event.preventDefault();
                return firstModalNode.current.focus();

            }

        },
        shiftCB: (event) => {

            if (event.target === firstModalNode.current) {

                event.preventDefault();
                return lastModalNode.current.focus();

            }

        },
    };

    useKeyPress(keysToTrack, keyPressCallbackProps);

    const {
        animated,
        width,
        height,
    } = useMedia(
        [`(min-width: ${BREAKPOINTS.md})`],
        [
            {
                animated: false,
                width: '721px',
                height: '86vh',
            },
        ],
        {
            animated: true,
            width: '100%',
            height: '99%',
        }
    );

    const handlers = useMemo(() => ({
        onOpened: () => {

            firstModalNode.current.focus();
            onOpened();

        },
        onClosed,
    }), [
        onOpened,
        onClosed,
        firstModalNode,
    ]);

    return (
        <Modal
            name={TRIM_REEL_CLIP_MODAL_NAME}
            animate={animated}
            animateFrom={animated ? AnimateFrom.BOTTOM : undefined}
            closeButtonRef={firstModalNode}
            headerTitle={trimVideo}
            width={width}
            height={height}
            overlay
            position={modalPosition}
            handlers={handlers}
        >
            <ClipTrimming submitButtonRef={lastModalNode} />
        </Modal>
    );

};

TrimmingModal.propTypes = {
    onClosed: PropTypes.func,
    onOpened: PropTypes.func,
};

TrimmingModal.defaultProps = {
    onClosed: _noop,
    onOpened: _noop,
};

export default TrimmingModal;
